import * as React from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface SectionNavSubDef {
  value: string;
  label: string;
  icon?: LucideIcon;
  badge?: React.ReactNode;
  disabled?: boolean;
}

export interface SectionNavSectionDef {
  value: string;
  label: string;
  icon?: LucideIcon;
  badge?: React.ReactNode;
  disabled?: boolean;
  subs?: SectionNavSubDef[];
}

export interface SectionNavValue {
  section: string;
  sub?: string;
}

export interface SectionNavProps {
  sections: SectionNavSectionDef[];
  value: SectionNavValue;
  onChange: (next: SectionNavValue) => void;
  sticky?: boolean;
  stickyOffset?: number;
  dense?: boolean;
  className?: string;
  ariaLabel?: string;
  ariaLabelSub?: string;
}

interface StripProps {
  items: { value: string; label: string; icon?: LucideIcon; badge?: React.ReactNode; disabled?: boolean }[];
  activeValue: string;
  onSelect: (value: string) => void;
  ariaLabel: string;
  level: 'parent' | 'sub';
  dense?: boolean;
  idPrefix: string;
}

const Strip = React.forwardRef<HTMLDivElement, StripProps>(function Strip(
  { items, activeValue, onSelect, ariaLabel, level, dense, idPrefix },
  ref,
) {
  const buttonsRef = React.useRef<Array<HTMLButtonElement | null>>([]);

  const enabledIndexes = React.useMemo(
    () => items.map((it, i) => (it.disabled ? -1 : i)).filter(i => i >= 0),
    [items],
  );

  const focusIndex = React.useCallback((idx: number) => {
    const btn = buttonsRef.current[idx];
    if (btn) {
      btn.focus();
      btn.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
  }, []);

  const handleKey = (e: React.KeyboardEvent<HTMLButtonElement>, currentIdx: number) => {
    if (enabledIndexes.length === 0) return;
    const pos = enabledIndexes.indexOf(currentIdx);
    let nextPos = pos;
    if (e.key === 'ArrowRight') nextPos = (pos + 1) % enabledIndexes.length;
    else if (e.key === 'ArrowLeft') nextPos = (pos - 1 + enabledIndexes.length) % enabledIndexes.length;
    else if (e.key === 'Home') nextPos = 0;
    else if (e.key === 'End') nextPos = enabledIndexes.length - 1;
    else return;
    e.preventDefault();
    const targetIdx = enabledIndexes[nextPos];
    if (targetIdx === undefined) return;
    focusIndex(targetIdx);
    const target = items[targetIdx];
    if (target) onSelect(target.value);
  };

  const isParent = level === 'parent';
  const padY = dense ? 'py-1.5' : isParent ? 'py-2.5' : 'py-1.5';
  const padX = isParent ? 'px-3.5' : 'px-3';
  const text = isParent ? 'text-sm' : 'text-xs';

  return (
    <div
      ref={ref}
      role="tablist"
      aria-label={ariaLabel}
      className={cn(
        'relative flex items-center gap-1 overflow-x-auto whitespace-nowrap',
        '[scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
        isParent
          ? 'border-b border-slate-200 dark:border-slate-800'
          : 'mt-1 border-b border-slate-100 dark:border-slate-800/60',
      )}
    >
      {items.map((it, idx) => {
        const Icon = it.icon;
        const active = it.value === activeValue;
        const tabId = `${idPrefix}-tab-${it.value}`;
        const panelId = `${idPrefix}-panel-${it.value}`;
        return (
          <button
            key={it.value}
            ref={el => { buttonsRef.current[idx] = el; }}
            id={tabId}
            type="button"
            role="tab"
            aria-selected={active}
            aria-controls={panelId}
            tabIndex={active ? 0 : -1}
            disabled={it.disabled}
            onClick={() => !it.disabled && onSelect(it.value)}
            onKeyDown={e => handleKey(e, idx)}
            className={cn(
              'inline-flex items-center gap-1.5 font-medium transition-colors duration-150',
              'border-b-2 -mb-px shrink-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500/40 rounded-t-md',
              padX,
              padY,
              text,
              active
                ? isParent
                  ? 'border-indigo-600 text-indigo-700 dark:border-indigo-400 dark:text-indigo-300'
                  : 'border-indigo-500 text-indigo-600 dark:border-indigo-400 dark:text-indigo-300'
                : 'border-transparent text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200',
              it.disabled && 'opacity-50 cursor-not-allowed',
            )}
          >
            {Icon ? <Icon className={cn('shrink-0', isParent ? 'h-4 w-4' : 'h-3.5 w-3.5')} /> : null}
            <span>{it.label}</span>
            {it.badge != null ? (
              <span className="ml-1 inline-flex items-center">{it.badge}</span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
});

export function SectionNav({
  sections,
  value,
  onChange,
  sticky,
  stickyOffset = 0,
  dense,
  className,
  ariaLabel = 'Sezioni',
  ariaLabelSub = 'Sottosezioni',
}: SectionNavProps) {
  const activeSection = sections.find(s => s.value === value.section) ?? sections[0];
  const subs = activeSection?.subs ?? [];

  React.useEffect(() => {
    if (!activeSection) return;
    if (subs.length === 0) {
      if (value.sub !== undefined) onChange({ section: activeSection.value, sub: undefined });
      return;
    }
    const hasCurrentSub = value.sub != null && subs.some(s => s.value === value.sub);
    if (!hasCurrentSub) {
      const firstSub = subs[0];
      if (firstSub) onChange({ section: activeSection.value, sub: firstSub.value });
    }
  }, [activeSection, subs, value.sub, onChange]);

  const handleParent = (next: string) => {
    const target = sections.find(s => s.value === next);
    if (!target) return;
    const firstSub = target.subs?.[0]?.value;
    onChange({ section: next, sub: firstSub });
  };

  const handleSub = (next: string) => {
    if (!activeSection) return;
    onChange({ section: activeSection.value, sub: next });
  };

  return (
    <div
      className={cn(
        'bg-white/95 dark:bg-slate-950/85 backdrop-blur supports-[backdrop-filter]:bg-white/70 dark:supports-[backdrop-filter]:bg-slate-950/60',
        sticky && 'sticky z-30',
        className,
      )}
      style={sticky ? { top: stickyOffset } : undefined}
    >
      <Strip
        items={sections}
        activeValue={activeSection?.value ?? ''}
        onSelect={handleParent}
        ariaLabel={ariaLabel}
        level="parent"
        dense={dense}
        idPrefix="section-nav"
      />
      {subs.length > 0 ? (
        <Strip
          items={subs}
          activeValue={value.sub ?? subs[0]?.value ?? ''}
          onSelect={handleSub}
          ariaLabel={ariaLabelSub}
          level="sub"
          dense={dense}
          idPrefix={`section-nav-${activeSection?.value ?? 'root'}`}
        />
      ) : null}
    </div>
  );
}
